/* BP-012 — Seller Finance Intelligence tab. Owner-financed acquisition modeling. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Download, Banknote } from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY = {
  purchase_price: "", down_payment: "", interest_rate: "", loan_term_years: "30",
  amortization_term_years: "", balloon_years: "", loan_type: "amortizing",
  monthly_rent: "", monthly_taxes: "0", monthly_insurance: "0",
  monthly_maintenance: "0", monthly_hoa: "0", closing_costs: "",
  desired_monthly_cash_flow: "300",
};

export default function SellerFinanceTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY,
    purchase_price: property.asking_price || "",
    monthly_rent: property.published_rent || "",
  });
  const [scenarioName, setScenarioName] = useState("Base Terms");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/seller-finance`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load Seller Finance"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const calc = useCallback(async (save) => {
    if (!inputs.purchase_price || !inputs.down_payment || !inputs.interest_rate || !inputs.monthly_rent) {
      toast.error("Purchase, Down Payment, Interest Rate, and Monthly Rent are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/seller-finance/calculate`,
        { inputs, save, scenario_name: scenarioName });
      if (save) { setCurrent(data); toast.success(`${data.scenario_name} saved`); load(); }
      else { setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName }); toast.success("Calculated"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/seller-finance/${current.id}/export/pdf`, "_blank");
  }, [current]);

  const outs = useMemo(() => current?.outputs || {}, [current]);
  const score = outs.seller_finance_score ? parseInt(String(outs.seller_finance_score), 10) : null;
  const band = score == null ? null
    : score >= 85 ? { c: "text-emerald-700", d: "bg-emerald-500", l: "EXCELLENT" }
    : score >= 70 ? { c: "text-lime-700",    d: "bg-lime-500",    l: "STRONG" }
    : score >= 55 ? { c: "text-amber-700",   d: "bg-amber-500",   l: "WORKABLE" }
    :                { c: "text-red-700",    d: "bg-red-500",     l: "RENEGOTIATE" };

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="sf-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-seller-finance">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <Banknote size={12}/> Owner-Financed Acquisition
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <F l="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="sf-purchase" className={INP} type="number"/></F>
          <F l="Down Payment *"><input value={inputs.down_payment} onChange={set("down_payment")} className={INP} type="number"/></F>
          <F l="Interest Rate (0.06 = 6%) *"><input value={inputs.interest_rate} onChange={set("interest_rate")} className={INP} type="number" step="0.001"/></F>
          <F l="Loan Term (yrs)"><input value={inputs.loan_term_years} onChange={set("loan_term_years")} className={INP} type="number"/></F>
          <F l="Amortization (yrs · blank = loan)"><input value={inputs.amortization_term_years} onChange={set("amortization_term_years")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Balloon (yrs · blank = none)"><input value={inputs.balloon_years} onChange={set("balloon_years")} className={INP} type="number" placeholder="none"/></F>
          <F l="Loan Type">
            <select value={inputs.loan_type} onChange={set("loan_type")} className={INP} data-testid="sf-loan-type">
              <option value="amortizing">Amortizing</option>
              <option value="interest_only">Interest-Only</option>
            </select>
          </F>
          <F l="Monthly Rent *"><input value={inputs.monthly_rent} onChange={set("monthly_rent")} data-testid="sf-rent" className={INP} type="number"/></F>
          <F l="Closing Costs (blank → 2%)"><input value={inputs.closing_costs} onChange={set("closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Monthly Taxes"><input value={inputs.monthly_taxes} onChange={set("monthly_taxes")} className={INP} type="number"/></F>
          <F l="Monthly Insurance"><input value={inputs.monthly_insurance} onChange={set("monthly_insurance")} className={INP} type="number"/></F>
          <F l="Monthly Maintenance"><input value={inputs.monthly_maintenance} onChange={set("monthly_maintenance")} className={INP} type="number"/></F>
          <F l="Monthly HOA"><input value={inputs.monthly_hoa} onChange={set("monthly_hoa")} className={INP} type="number"/></F>
          <F l="Desired Monthly CF"><input value={inputs.desired_monthly_cash_flow} onChange={set("desired_monthly_cash_flow")} className={INP} type="number"/></F>
          <F l="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} className={INP}/></F>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="sf-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>} Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating} data-testid="sf-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save
          </button>
          <button onClick={exportPdf} disabled={!current?.id} data-testid="sf-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {current && (<>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="sf-results">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-[240px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Monthly Cash Flow</div>
              <div className={`text-4xl font-bold tabular-nums mt-1 ${parseFloat(outs.monthly_cash_flow) < 0 ? "text-red-700" : "text-slate-900"}`}
                   data-testid="sf-cash-flow">${fmt(outs.monthly_cash_flow)}</div>
              <div className="text-xs text-slate-700 mt-1">
                DSCR <b>{ratio(outs.dscr)}</b> · CoC <b>{pct(outs.cash_on_cash)}</b>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                P&I <b>${fmt(outs.monthly_pi)}</b> · Balloon <b>${fmt(outs.balloon_balance)}</b>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Offer Range: <b>${fmt(outs.recommended_offer_low)}</b> – <b>${fmt(outs.recommended_offer_high)}</b>
              </div>
            </div>
            <div className="flex flex-col items-center min-w-[140px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Seller Finance Score</div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-5xl font-bold tabular-nums ${band?.c || "text-slate-400"}`} data-testid="sf-score">{score ?? "—"}</span>
                <span className="text-sm text-slate-500 font-medium">/100</span>
              </div>
              {band && (
                <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${band.c}`}>
                  <span className={`w-2 h-2 rounded-full ${band.d}`}/> {band.l}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
          <Metric l="Loan Amount" v={`$${fmt(outs.loan_amount)}`}/>
          <Metric l="Total Monthly Pmt" v={`$${fmt(outs.total_monthly_payment)}`}/>
          <Metric l="NOI (annual)" v={`$${fmt(outs.noi)}`}/>
          <Metric l="Cash Invested" v={`$${fmt(outs.cash_invested)}`}/>
          <Metric l="Total Interest" v={`$${fmt(outs.total_interest_paid)}`}/>
          <Metric l="Annual Cash Flow" v={`$${fmt(outs.annual_cash_flow)}`}/>
          <Metric l="Gross Annual Rent" v={`$${fmt(outs.gross_annual_income)}`}/>
          <Metric l="Annual OpEx" v={`$${fmt(outs.annual_op_ex)}`}/>
        </div>

        {(outs.warnings || []).length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="sf-warnings">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Warnings</div>
            {outs.warnings.map((w, i) => (
              <div key={i} className="text-[11px] text-slate-800">
                <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-semibold mr-2 ${
                  w.severity === "critical" ? "bg-red-100 text-red-700"
                  : w.severity === "high" ? "bg-orange-100 text-orange-700"
                  : "bg-amber-100 text-amber-700"
                }`}>{w.severity}</span>{w.message}
              </div>
            ))}
          </div>
        )}
      </>)}

      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY, ...a.inputs }); }}
                      data-testid={`sf-history-${a.id.slice(0,8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{a.version} · {a.scenario_name}</span>
                <span className="tabular-nums text-slate-600">
                  Score {a.outputs?.seller_finance_score || "—"} · CF/mo ${fmt(a.outputs?.monthly_cash_flow)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ l, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{l}</div>{children}
  </label>
);
const Metric = ({ l, v }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{v}</div>
  </div>
);
function fmt(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toLocaleString(undefined,{maximumFractionDigits:0}); }
function pct(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):`${(n*100).toFixed(1)}%`; }
function ratio(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toFixed(2); }
